/**
 * One catalogue, many trades.
 *
 * A "blueprint" describes what a kind of item actually is: which details matter,
 * how it is priced, how availability is counted, and what is worth teaching the
 * AI about it. The catalogue UI never changes shape — only the content it asks
 * for does. Merchants never see the words schema, attribute or variant.
 */

export type PricingModel =
  | 'rental'
  | 'sale'
  | 'retail'
  | 'menu'
  | 'unit'
  | 'service'
  | 'lease'

export type AvailabilityModel =
  | 'dates'
  | 'single'
  | 'stock'
  | 'today'
  | 'slots'

export interface CategoryBlueprint {
  id: string
  /** Shown beside the sub-category on a card, e.g. "Rental". */
  label: string
  /** Used in sentences: "What Talab quotes customers for this car." */
  itemNoun: string
  /** Sub-categories offered for this kind of item. */
  categories: string[]
  pricing: PricingModel
  availability: AvailabilityModel
  /** The details this trade cares about, in the order they should be asked. */
  detailFields: string[]
  /** Essentials asked for on the add flow's first screen. */
  essentials: string[]
  /** What would raise AI readiness for this kind of item. */
  readiness: string[]
  /** Natural-language knowledge suggestions for this kind of item. */
  knowledge: string[]
  /** Option groups a merchant would plausibly use. */
  optionGroups: { name: string; values: string[] }[]
}

const BLUEPRINTS: CategoryBlueprint[] = [
  {
    id: 'vehicle-rental',
    label: 'Rental',
    itemNoun: 'vehicle',
    categories: ['SUVs', 'Sedans', 'Hatchbacks', 'Luxury', 'Vans', 'Pickups'],
    pricing: 'rental',
    availability: 'dates',
    detailFields: [
      'Make',
      'Model',
      'Year',
      'Seats',
      'Transmission',
      'Fuel',
      'Colour',
    ],
    essentials: ['Year', 'Seats', 'Transmission'],
    readiness: ['Mileage limit', 'Insurance cover', 'More photos', 'Deposit'],
    knowledge: [
      'Airport delivery needs 24 hours notice',
      'Free delivery for rentals over 7 days',
      'Minimum rental period is 2 days',
      'Driver must be 25 or older',
    ],
    optionGroups: [
      {
        name: 'Rental period',
        values: ['Daily', 'Weekly', 'Monthly'],
      },
    ],
  },
  {
    id: 'vehicle-sale',
    label: 'For sale',
    itemNoun: 'vehicle',
    categories: ['SUVs', 'Sedans', 'Luxury', 'Pickups', 'Electric'],
    pricing: 'sale',
    availability: 'single',
    detailFields: [
      'Make',
      'Model',
      'Year',
      'Mileage',
      'Condition',
      'Trim',
      'Engine',
      'Warranty',
    ],
    essentials: ['Year', 'Mileage', 'Condition'],
    readiness: ['Service history', 'Warranty', 'More photos', 'Accident history'],
    knowledge: [
      'Finance available from 20% down payment',
      'Trade-ins accepted against this vehicle',
      'Inspection report available on request',
    ],
    optionGroups: [
      {
        name: 'Payment',
        values: ['Cash', 'Finance', 'Trade-in'],
      },
      {
        name: 'Colour',
        values: ['White', 'Black', 'Silver', 'Grey'],
      },
    ],
  },
  {
    id: 'perfume',
    label: 'Fragrance',
    itemNoun: 'fragrance',
    categories: ['Signature', 'Oud', 'Floral', 'Fresh', 'Gift Sets'],
    pricing: 'retail',
    availability: 'stock',
    detailFields: [
      'Brand',
      'Concentration',
      'Size',
      'Fragrance family',
      'Gender',
      'Main notes',
      'Longevity',
    ],
    essentials: ['Brand', 'Size', 'Concentration'],
    readiness: ['Fragrance notes', 'Concentration', 'Bottle sizes', 'Longevity'],
    knowledge: [
      'Recommend to customers who like woody and spicy scents',
      'Lasts 8 hours or more on skin',
      'Gift wrapping available at no charge',
    ],
    optionGroups: [
      {
        name: 'Bottle size',
        values: ['50 ml', '100 ml', '200 ml'],
      },
    ],
  },
  {
    id: 'clothing',
    label: 'Apparel',
    itemNoun: 'item',
    categories: ['Shirts', 'Dresses', 'Outerwear', 'Trousers', 'Accessories'],
    pricing: 'retail',
    availability: 'stock',
    detailFields: [
      'Brand',
      'Material',
      'Fit',
      'Gender',
      'Season',
      'Care',
    ],
    essentials: ['Brand', 'Material', 'Fit'],
    readiness: ['Size chart', 'Material', 'Available colours', 'Fit notes'],
    knowledge: [
      'This fit runs slightly small — recommend one size larger',
      'Machine washable at 30 degrees',
      'Free exchange on sizes within 14 days',
    ],
    optionGroups: [
      {
        name: 'Size',
        values: ['S', 'M', 'L', 'XL'],
      },
      {
        name: 'Colour',
        values: ['Black', 'White', 'Sand'],
      },
    ],
  },
  {
    id: 'restaurant',
    label: 'Menu item',
    itemNoun: 'dish',
    categories: ['Burgers', 'Mains', 'Starters', 'Desserts', 'Drinks'],
    pricing: 'menu',
    availability: 'today',
    detailFields: [
      'Cuisine',
      'Portion',
      'Ingredients',
      'Allergens',
      'Preparation time',
      'Dietary',
      'Spice level',
    ],
    essentials: ['Portion', 'Preparation time', 'Allergens'],
    readiness: ['Allergens', 'Ingredients', 'Preparation time', 'Photo'],
    knowledge: [
      'Can be made without cheese but not fully dairy-free',
      'Prepared fresh — allow 20 minutes at peak hours',
      'Contains nuts in the sauce',
    ],
    optionGroups: [
      {
        name: 'Size',
        values: ['Regular', 'Large'],
      },
      {
        name: 'Extras',
        values: ['Extra cheese', 'Bacon', 'Truffle fries'],
      },
    ],
  },
  {
    id: 'grocery',
    label: 'Grocery',
    itemNoun: 'product',
    categories: ['Fresh Produce', 'Dairy', 'Bakery', 'Pantry', 'Beverages'],
    pricing: 'unit',
    availability: 'stock',
    detailFields: [
      'Brand',
      'Weight',
      'Unit',
      'Pack size',
      'Origin',
      'Storage',
      'Expiry',
    ],
    essentials: ['Brand', 'Weight', 'Pack size'],
    readiness: ['Pack size', 'Storage advice', 'Origin', 'Photo'],
    knowledge: [
      'Delivered chilled — keep refrigerated on arrival',
      'Bulk discount applies from 12 units',
      'Sourced locally and restocked daily',
    ],
    optionGroups: [
      {
        name: 'Pack size',
        values: ['Single', '6-pack', '24-pack'],
      },
    ],
  },
  {
    id: 'electronics',
    label: 'Electronics',
    itemNoun: 'product',
    categories: ['Phones', 'Laptops', 'Audio', 'Wearables', 'Accessories'],
    pricing: 'retail',
    availability: 'stock',
    detailFields: [
      'Brand',
      'Model',
      'Storage',
      'Colour',
      'Warranty',
      'Condition',
    ],
    essentials: ['Brand', 'Model', 'Warranty'],
    readiness: ['Warranty terms', 'Full specifications', 'In-box items'],
    knowledge: [
      'Includes a one-year regional warranty',
      'Installments available over 6 or 12 months',
      'Trade-in accepted against this model',
    ],
    optionGroups: [
      {
        name: 'Storage',
        values: ['128GB', '256GB', '512GB'],
      },
      {
        name: 'Colour',
        values: ['Black', 'Silver'],
      },
    ],
  },
  {
    id: 'service',
    label: 'Service',
    itemNoun: 'service',
    categories: ['Consultation', 'Maintenance', 'Installation', 'Delivery'],
    pricing: 'service',
    availability: 'slots',
    detailFields: ['Duration', 'Where', 'Included', 'Team size', 'Notice'],
    essentials: ['Duration', 'Where', 'Included'],
    readiness: ['What is included', 'Duration', 'Notice needed'],
    knowledge: [
      'Book at least 24 hours ahead',
      'Covers Dubai and Abu Dhabi at no extra charge',
      'Cancellation is free up to 6 hours before',
    ],
    optionGroups: [
      {
        name: 'Duration',
        values: ['30 min', '1 hour', 'Half day'],
      },
    ],
  },
  {
    id: 'property-sale',
    label: 'For sale',
    itemNoun: 'property',
    categories: [
      'Apartments',
      'Villas',
      'Townhouses',
      'Penthouses',
      'Offices',
      'Plots',
    ],
    pricing: 'sale',
    // A property is one of a kind, so it is available or it is not.
    availability: 'single',
    detailFields: [
      'Bedrooms',
      'Bathrooms',
      'Size',
      'Community',
      'Developer',
      'Handover',
      'Furnishing',
      'Parking',
      'View',
      'Ownership',
    ],
    essentials: ['Bedrooms', 'Bathrooms', 'Size'],
    readiness: [
      'Floor plan',
      'Service charge',
      'Handover date',
      'More photos',
      'Community amenities',
    ],
    knowledge: [
      'Freehold and available to all nationalities',
      'Service charge is AED 14 per sq ft per year',
      'Viewings available daily between 10am and 7pm',
      'Mortgage pre-approval can be arranged in-house',
    ],
    optionGroups: [
      {
        name: 'Payment plan',
        values: ['Cash', '20 / 80 plan', 'Mortgage'],
      },
    ],
  },
  {
    id: 'property-rent',
    label: 'To rent',
    itemNoun: 'property',
    categories: [
      'Apartments',
      'Villas',
      'Townhouses',
      'Studios',
      'Offices',
      'Short stay',
    ],
    pricing: 'lease',
    availability: 'dates',
    detailFields: [
      'Bedrooms',
      'Bathrooms',
      'Size',
      'Community',
      'Furnishing',
      'Parking',
      'Available from',
      'Cheques',
      'View',
    ],
    essentials: ['Bedrooms', 'Bathrooms', 'Size'],
    readiness: [
      'Available from date',
      'Number of cheques',
      'What bills are included',
      'Floor plan',
      'More photos',
    ],
    knowledge: [
      'Rent can be paid in up to 4 cheques',
      'Chiller and DEWA are paid by the tenant',
      'Minimum lease is 12 months',
      'Agency fee is 5% of the annual rent',
    ],
    optionGroups: [
      {
        name: 'Payment terms',
        values: ['1 cheque', '2 cheques', '4 cheques'],
      },
      {
        name: 'Furnishing',
        values: ['Unfurnished', 'Furnished'],
      },
    ],
  },
]

export function blueprint(kind: string): CategoryBlueprint {
  return BLUEPRINTS.find((b) => b.id === kind) ?? BLUEPRINTS[5]
}

export const ALL_BLUEPRINTS = BLUEPRINTS

/** A merchant's business type gives us a sensible starting point. */
export const DEFAULT_KIND: Record<string, string> = {
  car_rental: 'vehicle-rental',
  car_dealer: 'vehicle-sale',
  real_estate: 'property-rent',
  restaurant: 'restaurant',
  retail: 'clothing',
  grocery: 'grocery',
  electronics: 'electronics',
  beauty: 'perfume',
  services: 'service',
  generic: 'grocery',
}

/** Availability, in the words each trade actually uses. */
export const AVAILABILITY_COPY: Record<
  AvailabilityModel,
  {
    label: string
    counts: boolean
    none: string
    low: (n: number) => string
    plenty: (n: number) => string
  }
> = {
  dates: {
    label: 'Availability',
    counts: false,
    none: 'Fully booked',
    low: () => 'Limited dates',
    plenty: () => 'Available',
  },
  single: {
    label: 'Availability',
    counts: false,
    none: 'Sold',
    low: () => 'Reserved',
    plenty: () => '1 available',
  },
  stock: {
    label: 'Stock',
    counts: true,
    none: 'Out of stock',
    low: (n) => `Only ${n} left`,
    plenty: (n) => `${n} in stock`,
  },
  today: {
    label: 'Availability',
    counts: false,
    none: 'Off the menu today',
    low: () => 'Limited today',
    plenty: () => 'Available today',
  },
  slots: {
    label: 'Availability',
    counts: true,
    none: 'No slots left',
    low: (n) => `${n} slots left`,
    plenty: (n) => (n ? `${n} slots available` : 'Slots available'),
  },
}

/** The pricing model each trade leads with, plus what it can opt into. */
export const PRICING_FIELDS: Record<
  PricingModel,
  { lead: string; leadHint?: string; optional: string[] }
> = {
  rental: {
    lead: 'Daily rate',
    optional: ['Weekly rate', 'Monthly rate', 'Deposit', 'Delivery charge'],
  },
  sale: {
    lead: 'Sale price',
    leadHint: 'The price a customer pays today.',
    optional: ['Was price', 'Down payment', 'Monthly finance', 'Registration fee'],
  },
  retail: {
    lead: 'Price',
    optional: ['Sale price', 'Gift wrap', 'Delivery charge'],
  },
  menu: {
    lead: 'Price',
    leadHint: 'The price of the regular portion.',
    optional: ['Large portion', 'Extras', 'Delivery charge'],
  },
  unit: {
    lead: 'Unit price',
    leadHint: 'What one of these costs.',
    optional: ['Pack price', 'Bulk discount', 'Delivery charge'],
  },
  service: {
    lead: 'Price',
    leadHint: 'What this service costs per booking.',
    optional: ['Callout fee', 'Out-of-hours rate', 'Travel charge'],
  },
  lease: {
    lead: 'Annual rent',
    leadHint: 'What a tenant pays for a year.',
    optional: [
      'Monthly rent',
      'Security deposit',
      'Agency fee',
      'Chiller fees',
    ],
  },
}
